<template>
  <!-- Pinned side panel (desktop, ≥lg) / full-screen (mobile & tablet, <lg).
       Mounted only while open, so closing discards the conversation (v1:
       persist nothing). The host lives once in the layout, so the panel
       survives client-side navigation while open.
       Desktop: flush to the right viewport edge, full height, separated from the
       (squeezed) page by a left border. The page squeeze itself lives in the
       layout — see default.vue.
       z-10 puts the panel on the shared top app layer. The DS modal & backdrop
       (teleported to <body>, after the app root) sit at the same z-10 but paint
       later, so dialogs/drawers/overlays always render on top of the assistant.
       The page header (TheHeader) is also z-10; the host is rendered last in the
       layout so, at equal z-index, the panel paints above the header. Don't raise
       this above z-10, or the panel would cover the DS modals again.
       Mobile/tablet height/transform come from panelStyle (useVisualViewport),
       not the h-[100dvh] class below: 100dvh doesn't shrink for the on-screen
       keyboard, which is what h-[100dvh] alone used to leave cropped. -->
  <Transition
    enter-active-class="lg:transition-transform lg:duration-[250ms] lg:ease-out"
    enter-from-class="lg:translate-x-full"
    leave-active-class="lg:transition-transform lg:duration-[250ms] lg:ease-out"
    leave-to-class="lg:translate-x-full"
  >
    <div
      v-if="store.isOpen"
      aria-label="Back Market AI"
      :aria-modal="!isDesktop"
      class="bg-surface-default-low mood-warm fixed z-10 overflow-hidden"
      :class="
        isDesktop
          ? 'border-static-default-low right-0 top-0 h-[100dvh] border-l'
          : 'inset-0 h-[100dvh]'
      "
      role="dialog"
      :style="panelStyle"
    >
      <div class="relative flex h-full flex-col overflow-hidden">
        <ShoppingAssistantChat
          :key="store.conversationKey"
          :can-resume-latest="false"
          :entry-point="store.entryPoint!"
          :initial-chat-id="null"
          :is-seeded-launch
          :page-context="store.pageContext"
          :seeded-message="store.seededMessage"
        />
      </div>
    </div>
  </Transition>

  <!-- Shared confirmation for both new-chat and close (gated on a non-empty
       conversation by the web-sidebar host adapter). Rendered above the panel
       by the DS portal so it is never occluded. -->
  <RevDialog
    :name="WEB_SIDEBAR_CONFIRM_DIALOG_NAME"
    :title="currentDialog?.title ?? ''"
  >
    <template #body>
      <p class="body-1">{{ currentDialog?.description }}</p>
    </template>

    <template #footer>
      <RevButton full-width="always" variant="primary" @click="acceptDialog">
        {{ currentDialog?.acceptButtonText }}
      </RevButton>
      <RevButton
        class="mt-12"
        full-width="always"
        variant="secondary"
        @click="declineDialog"
      >
        {{ currentDialog?.declineButtonText }}
      </RevButton>
    </template>
  </RevDialog>
</template>

<script lang="ts" setup>
import { computed, provide } from 'vue'

import { RevButton } from '@ds/components/Button'
import { RevDialog } from '@ds/components/Dialog'

import { useVisualViewport } from '../../webviews/useVisualViewport'
import { useShoppingAssistantBrowsingContext } from '../composables/useShoppingAssistantBrowsingContext'
import { SHOPPING_ASSISTANT_HOST_KEY } from '../composables/useShoppingAssistantHost.injection-key'
import { useShoppingAssistantViewport } from '../composables/useShoppingAssistantViewport'
import {
  WEB_SIDEBAR_CONFIRM_DIALOG_NAME,
  useWebSidebarShoppingAssistantHost,
} from '../composables/useWebSidebarShoppingAssistantHost'
import { ASSISTANT_PANEL_WIDTH_PX } from '../constants'
import ShoppingAssistantChat from '../pages/ShoppingAssistantChat.vue'
import { useShoppingAssistantWebSidebar } from '../stores/useShoppingAssistantWebSidebar'

const store = useShoppingAssistantWebSidebar()
const { isDesktop, isOverlay } = useShoppingAssistantViewport()

// This host lives once in the layout and never unmounts with the panel, so
// the keyboard-avoidance tracking below is only actually enabled while the
// panel is open — see useVisualViewport's `enabled` param.
const { style: viewportStyle } = useVisualViewport(() => isOverlay.value)

const { host, currentDialog, acceptDialog, declineDialog, chatId } =
  useWebSidebarShoppingAssistantHost()
provide(SHOPPING_ASSISTANT_HOST_KEY, host)

useShoppingAssistantBrowsingContext(chatId)

const isSeededLaunch = computed(
  () => Boolean(store.seededMessage) || Boolean(store.pageContext),
)

const panelStyle = computed(() =>
  isDesktop.value
    ? { width: `${ASSISTANT_PANEL_WIDTH_PX}px` }
    : viewportStyle.value,
)
</script>
